import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcPlaidV1ProcessorTokenQueryServiceProcessorTokenGet } from '@interxfi/api-client/generated/hooks';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcPlaidV1ProcessorTokenState } from '@interxfi/types';
import { PlaidProcessorTokenEntityMenu } from '../../../../../../../../../../features/vendor/plaid/processor-token/plaid-processor-token-entity-menu';
import { PlaidProcessorTokenEventSummary } from '../../../../../../../../../../features/vendor/plaid/processor-token/plaid-processor-token-event';
import { getPlaidProcessorTokenStateListItems } from '../../../../../../../../../../features/vendor/plaid/processor-token/plaid-processor-token-list-items';
import { getPlaidProcessorTokenDetailPath } from '../../../../../../../../../../features/vendor/plaid/processor-token/plaid-processor-token-routing';
import { PlaidProcessorTokenStatusPill } from '../../../../../../../../../../features/vendor/plaid/processor-token/plaid-processor-token-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/plaid/processor-token/_processor-token/$processorTokenId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `plaid-processor-token-${params.processorTokenId}`,
        label: 'Processor Token',
        to: getPlaidProcessorTokenDetailPath(params.processorTokenId),
        suffix: (
          <CopyButton
            content={params.processorTokenId}
            label="Copy Processor Token ID"
            tooltip={{ copy: params.processorTokenId, copied: 'Copied Processor Token ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Plaid Processor Token'),
  component: PlaidProcessorTokenDetail,
});

function getListItems(data: ExcPlaidV1ProcessorTokenState | undefined) {
  const items = getPlaidProcessorTokenStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [
          items.data.spec.processorToken,
          items.data.lastVerified,
          items.data.spec.accountFromPlaid.accountId,
          items.data.spec.accountFromPlaid.name,
          items.data.spec.accountFromPlaid.mask,
          items.data.spec.accountFromPlaid.officialName,
          items.data.spec.accountFromPlaid.accountType,
          items.data.spec.accountFromPlaid.subtype,
          items.data.spec.accountFromPlaid.ach.accountNumber,
          items.data.spec.accountFromPlaid.ach.routingNumber,
          items.data.spec.accountFromPlaid.ach.wireRoutingNumber,
        ],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.processorTokenId,
      items.programId,
      items.accountId,
      items.data.sourceId,
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load Plaid Processor Token', 'view this Plaid Processor Token');

function PlaidProcessorTokenDetail() {
  const { processorTokenId } = Route.useParams();
  const request = { processorTokenId } as const;
  const { data, error, isPending } = useExcPlaidV1ProcessorTokenQueryServiceProcessorTokenGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.processorToken), [data?.processorToken]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <PlaidProcessorTokenStatusPill isPending={showPending} size="large" status={data?.processorToken?.status} />
            </HorizontalStack>

            <PlaidProcessorTokenEntityMenu {...request} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <PlaidProcessorTokenEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
